import { useEffect, useMemo, useState } from 'react';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { GroupFilterSelect, UNGROUPED_LABEL } from '@/components/form/GroupFilterSelect';
import { SearchBox } from '@/components/form/SearchBox';
import { fetchTestCatalog, fetchTestCatalogGroups, type TestCatalogRow } from '@/api/testCatalog';
import type { ClientsPageMeta } from '@/api/clients';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useGroupOptions } from '@/hooks/useGroupOptions';
import { formatBalance } from '@/lib/clientFormat';
import type { WizardState } from './wizardState';

type PriceScope = 'library' | 'monoscale';

const PRICE_SCOPES: { key: PriceScope; label: string }[] = [
  { key: 'library', label: 'Тесты' },
  { key: 'monoscale', label: 'Моношкальные тесты' },
];

interface PriceStepRow {
  id: number;
  name: string;
  category: string | null;
  basePrice: number;
  clientPrice: number;
}

function matchesFilter(
  row: { name: string; category: string | null },
  search: string,
  group: string,
  ungroupedValue: string | null,
): boolean {
  if (search && !row.name.toLowerCase().includes(search.toLowerCase())) return false;
  if (group) return group === ungroupedValue ? row.category === null : row.category === group;
  return true;
}

/**
 * Шаг 5 — та же пара вложенных вкладок, что PricesPanel в ClientDetailPage.tsx,
 * но укороченная таблица (тест/базовая цена/цена для клиента, без версии,
 * группы и "изменено") и цена сразу редактируется инлайн (дефолт — базовая
 * цена). «Тесты» — только отмеченные на шаге 4 (фильтруются на клиенте, без
 * отдельного запроса — их и так уже мало); «Моношкальные тесты» — полный
 * каталог, доступны без подключения (как и в редактировании).
 */
export function StepPrices({
  state,
  onChange,
}: {
  state: WizardState;
  onChange: (state: WizardState) => void;
}) {
  const [scope, setScope] = useState<PriceScope>('library');
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [group, setGroup] = useState('');
  // Пагинация — только для "Моношкальные тесты" (реальный запрос к каталогу).
  // "Тесты" — локальный список из отмеченных на шаге 4 (обычно короткий,
  // ограничен собственным выбором пользователя), без сети и без страниц.
  const [page, setPage] = useState(1);
  const { groups, ungroupedValue } = useGroupOptions(() => fetchTestCatalogGroups(scope), [scope]);
  const [monoscaleCatalog, setMonoscaleCatalog] = useState<TestCatalogRow[] | null>(null);
  const [monoscaleMeta, setMonoscaleMeta] = useState<ClientsPageMeta | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Новый scope/поиск/фильтр — начинаем с первой страницы.
  useEffect(() => {
    setPage(1);
  }, [scope, search, group]);

  useEffect(() => {
    if (scope !== 'monoscale') return;
    let cancelled = false;
    setMonoscaleCatalog(null);
    fetchTestCatalog('monoscale', { search: search || undefined, group: group || undefined, page })
      .then(({ items, meta }) => {
        if (!cancelled) {
          setMonoscaleCatalog(items);
          setMonoscaleMeta(meta);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить каталог моношкальных тестов.');
      });
    return () => {
      cancelled = true;
    };
  }, [scope, search, group, page]);

  const rows: PriceStepRow[] | null = useMemo(() => {
    if (scope === 'library') {
      return Array.from(state.selectedTests.values())
        .map((t) => ({
          id: t.id,
          name: t.name,
          category: t.category,
          basePrice: t.basePrice,
          clientPrice: state.libraryPrices.get(t.id) ?? t.basePrice,
        }))
        .filter((r) => matchesFilter(r, search, group, ungroupedValue));
    }
    if (!monoscaleCatalog) return null;
    return monoscaleCatalog.map((t) => ({
      id: t.id,
      name: t.name,
      category: t.category,
      basePrice: t.basePrice,
      clientPrice: state.monoscalePrices.get(t.id) ?? t.basePrice,
    }));
  }, [scope, search, group, ungroupedValue, state.selectedTests, state.libraryPrices, monoscaleCatalog, state.monoscalePrices]);

  const setPrice = (id: number, cost: number) => {
    const key = scope === 'library' ? 'libraryPrices' : 'monoscalePrices';
    const nextMap = new Map(state[key]);
    nextMap.set(id, cost);
    onChange({ ...state, [key]: nextMap });
  };

  return (
    <div>
      <div className="tabs__list tabs__list--nested" role="tablist">
        {PRICE_SCOPES.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={scope === s.key}
            className={`tabs__item${scope === s.key ? ' tabs__item--active' : ''}`}
            onClick={() => {
              setScope(s.key);
              setGroup('');
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="filter-row">
        <SearchBox value={searchInput} onChange={setSearchInput} />
        <GroupFilterSelect groups={groups} ungroupedValue={ungroupedValue} value={group} onChange={setGroup} />
      </div>

      {error && <p className="field-error">{error}</p>}

      {rows === null ? (
        <div className="panel-loading">
          <Spinner />
        </div>
      ) : (
        <div className="card card--flush">
          <div className="table-wrap">
            <table className="table">
              <colgroup>
                <col style={{ width: '46%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '19%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Тест</th>
                  <th>Группа</th>
                  <th>Базовая цена</th>
                  <th>Цена для клиента</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="table__empty">
                      {scope === 'library'
                        ? 'Сначала выберите тесты на шаге «Тесты».'
                        : 'Моношкальные тесты не найдены.'}
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id}>
                      <td className="cell-primary">{row.name}</td>
                      <td className="cell-muted">{row.category ?? UNGROUPED_LABEL}</td>
                      <td className="cell-muted tabular">{formatBalance(row.basePrice)}</td>
                      <td className="tabular">
                        <input
                          type="number"
                          min={0}
                          className="test-list__cost-input"
                          value={row.clientPrice}
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            if (Number.isFinite(n) && n >= 0) setPrice(row.id, n);
                          }}
                          aria-label={`Цена для клиента: «${row.name}»`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {scope === 'monoscale' && monoscaleMeta && monoscaleMeta.lastPage > 1 && (
        <div className="table-footer">
          <span className="table-footer__status">
            Страница {monoscaleMeta.currentPage} из {monoscaleMeta.lastPage} · всего {monoscaleMeta.total}
          </span>
          <Pager page={monoscaleMeta.currentPage} lastPage={monoscaleMeta.lastPage} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
